import * as React from 'react';
import {Suspense} from 'react';
import {graphql, useLazyLoadQuery} from 'react-relay';
import {Link} from 'react-router-dom';
import {OpsTalentPageQuery} from './__generated__/OpsTalentPageQuery.graphql';
import {primaryButton} from './lib';

const QUERY = graphql`
    query OpsTalentPageQuery {
        talentProfiles {
            id
            name
            slug
            roles
            skills
            dayRate
            hourRate
            availability
            published
            featured
            position
            updatedAt
        }
    }`;

const Table: React.FC = () => {
    const data = useLazyLoadQuery<OpsTalentPageQuery>(QUERY, {}, {fetchPolicy: 'network-only'});
    const rows = data.talentProfiles;

    if (rows.length === 0) {
        return <p className="text-sm text-gray-500">No crew yet. Add the first profile.</p>;
    }

    return (
        <div className="overflow-x-auto border border-gray-800">
            <table className="w-full text-sm">
                <thead className="bg-black/50 text-left text-[11px] uppercase tracking-wider text-gray-500">
                <tr>
                    <th className="px-3 py-2">Name</th>
                    <th className="px-3 py-2">Roles</th>
                    <th className="px-3 py-2">Skills</th>
                    <th className="px-3 py-2">Rates</th>
                    <th className="px-3 py-2">Availability</th>
                    <th className="px-3 py-2">Visible</th>
                </tr>
                </thead>
                <tbody className="divide-y divide-gray-900">
                {rows.map((t) => (
                    <tr key={t.id} className="hover:bg-white/[0.03]">
                        <td className="px-3 py-2">
                            <Link to={`/ops/talent/${encodeURIComponent(t.id)}`} className="text-white hover:text-lumina-accent">{t.name}</Link>
                            <span className="block font-mono text-[10px] text-gray-500">/{t.slug}</span>
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-300">{t.roles.map((r) => r.replace(/_/g, ' ').toLowerCase()).join(', ')}</td>
                        <td className="px-3 py-2 text-xs text-gray-400">{t.skills.slice(0, 4).join(', ')}{t.skills.length > 4 ? '…' : ''}</td>
                        <td className="px-3 py-2 text-xs font-mono text-gray-300">
                            {t.dayRate ? `$${t.dayRate}/day` : ''}{t.dayRate && t.hourRate ? ' · ' : ''}{t.hourRate ? `$${t.hourRate}/hr` : ''}
                            {!t.dayRate && !t.hourRate ? <span className="text-gray-600">—</span> : null}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-400">{t.availability.toLowerCase()}</td>
                        <td className="px-3 py-2 text-xs">
                            {t.published ? <span className="text-emerald-400">Published</span> : <span className="text-gray-500">Draft</span>}
                            {t.featured && <span className="ml-2 text-lumina-accent">Featured</span>}
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    );
};

const OpsTalentPage: React.FC = () => (
    <div>
        <div className="flex items-end justify-between gap-4 mb-6">
            <h1 className="text-3xl font-display font-bold">Talent</h1>
            <Link to="/ops/talent/new" className={primaryButton}>New profile</Link>
        </div>
        <Suspense fallback={<p className="text-gray-500 text-sm">Loading…</p>}>
            <Table/>
        </Suspense>
    </div>
);

export default OpsTalentPage;
